import React from 'react';
import { Camera, Image as ImageIcon, Mic, X, Play, Pause, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../ui/utils';

interface ProductionPhoto {
    id: string;
    url: string;
    label: string;
}

interface VoiceMemoItem {
    id: string;
    url: string;
    duration: number;
    filename: string;
}

interface MediaUploadProps {
    photos: ProductionPhoto[];
    voiceMemos: VoiceMemoItem[];
    onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemovePhoto: (id: string) => void;
    onRecordVoice?: () => void; // Optional if handled by separate button
    isUploadingPhoto: boolean;
    isDarkMode: boolean;
    // Refs for triggering inputs
    galleryRef: React.RefObject<HTMLInputElement>;
    cameraRef: React.RefObject<HTMLInputElement>;
    openCamera: () => void;
    openGallery: () => void;
}

export function MediaUpload({
    photos,
    voiceMemos,
    onPhotoUpload,
    onRemovePhoto,
    isUploadingPhoto,
    isDarkMode,
    galleryRef,
    cameraRef,
    openCamera,
    openGallery
}: MediaUploadProps) {
    return (
        <div className="space-y-4">
            {/* Action Bar */}
            <div className="flex gap-3">
                <button
                    onClick={openCamera}
                    disabled={isUploadingPhoto}
                    className={cn(
                        "flex-1 flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border border-dashed transition-all hover:scale-[1.02] active:scale-95",
                        isDarkMode
                            ? "border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300"
                            : "border-gray-300 bg-gray-50 hover:bg-white text-gray-600"
                    )}
                >
                    <Camera className="w-6 h-6 text-blue-500" />
                    <span className="text-xs font-medium">Take Photo</span>
                </button>
                <button
                    onClick={openGallery}
                    disabled={isUploadingPhoto}
                    className={cn(
                        "flex-1 flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border border-dashed transition-all hover:scale-[1.02] active:scale-95",
                        isDarkMode
                            ? "border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300"
                            : "border-gray-300 bg-gray-50 hover:bg-white text-gray-600"
                    )}
                >
                    <ImageIcon className="w-6 h-6 text-purple-500" />
                    <span className="text-xs font-medium">Gallery</span>
                </button>
                {/* Inputs hidden */}
                <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={galleryRef}
                    onChange={onPhotoUpload}
                    multiple
                />
                <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    ref={cameraRef}
                    onChange={onPhotoUpload}
                />
            </div>

            {/* Photo Preview Grid */}
            <AnimatePresence>
                {photos.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="grid grid-cols-2 gap-3"
                    >
                        {photos.map((photo) => (
                            <motion.div
                                key={photo.id}
                                layout
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="relative aspect-video rounded-xl overflow-hidden group"
                            >
                                <img
                                    src={photo.url}
                                    alt={photo.label}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button
                                        onClick={() => onRemovePhoto(photo.id)}
                                        className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-600 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Voice Memos List could go here if managed by this component, 
                but DataEntry seemed to treat them separately or less prominently? 
                Adding them for completeness if needed. */}
            {voiceMemos.length > 0 && (
                <div className="space-y-2">
                    <h4 className={cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-slate-500" : "text-gray-400")}>
                        Voice Memos
                    </h4>
                    {voiceMemos.map(memo => (
                        <div key={memo.id} className={cn(
                            "flex items-center gap-3 p-3 rounded-xl border",
                            isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"
                        )}>
                            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600">
                                <Play className="w-4 h-4" fill="currentColor" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={cn("text-sm truncate", isDarkMode ? "text-slate-200" : "text-gray-700")}>
                                    {memo.filename || 'Voice Memo'}
                                </p>
                                <p className={cn("text-xs", isDarkMode ? "text-slate-500" : "text-gray-500")}>
                                    {memo.duration}s
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
